import React, { useContext, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import HomeIcon from "@mui/icons-material/Home";
import PhoneIcon from "@mui/icons-material/Phone";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import useAuthQuery from "../../store/auth/useAuthQuery";
import { variableContext } from "../../context/VariableContex";
import white from "../../assets/white.png";
import Flag from "../../assets/Flag.svg";
import "../../pages/myListing/Listing/Add business/AddBusinessGate.css";

/**
 * AuthGate — shared authentication gate.
 *
 * Props:
 *   children   — rendered inline when authenticated
 *   redirectTo — path to push after auth instead of rendering children
 */
const AuthGate = ({ children, redirectTo }) => {
  const history = useHistory();
  const { setMobile } = useContext(variableContext);

  const isLoggedIn =
    !!localStorage.getItem("accessToken") &&
    !!JSON.parse(localStorage.getItem("user") || "null")?.id;

  const [authed, setAuthed] = useState(isLoggedIn);

  /* ── OTP section state ── */
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [step, setStep] = useState("phone"); // "phone" | "otp"
  const [loading, setLoading] = useState(false);
  const [otp1, setOtp1] = useState("");
  const [otp2, setOtp2] = useState("");
  const [otp3, setOtp3] = useState("");
  const [otp4, setOtp4] = useState("");
  const [otpError, setOtpError] = useState("");

  /* ── Password-login section state ── */
  const [pwUsername, setPwUsername] = useState("");
  const [pwPassword, setPwPassword] = useState("");
  const [pwShowPass, setPwShowPass] = useState(false);
  const [pwError, setPwError] = useState("");
  const [pwLoading, setPwLoading] = useState(false);

  const otp2Ref = useRef();
  const otp3Ref = useRef();
  const otp4Ref = useRef();

  /* ── Mutations ── */
  const { mutateAsync: registerUser, isSuccess: regSuccess, isError: regIsError, error: regError } =
    useAuthQuery.Registration();
  const { mutateAsync: sendOtp, isSuccess: otpSent, isError: otpIsError, error: otpSendError } =
    useAuthQuery.SendOtp();
  const { mutateAsync: verifyOtp, isSuccess: verifySuccess, isError: verifyIsError, error: verifyError } =
    useAuthQuery.VerifyOtp();
  const { mutateAsync: passwordLogin } =
    useAuthQuery.PasswordLogin();

  /* ── Effects ── */
  useEffect(() => {
    if (regSuccess || otpSent) { setStep("otp"); setLoading(false); }
  }, [regSuccess, otpSent]);

  useEffect(() => {
    if (regIsError && regError) {
      // Show more detailed error for debugging
      const errorData = regError?.response?.data;
      let errorMessage = "Registration failed. Please try again.";
      
      if (errorData) {
        // Check for name field error specifically
        if (errorData.name) {
          errorMessage = "Registration temporarily unavailable. Please try Sign In instead.";
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.mobile) {
          errorMessage = errorData.mobile[0];
        }
      }
      
      setPhoneError(errorMessage);
      setLoading(false);
    }
  }, [regIsError, regError]);

  useEffect(() => {
    if (otpIsError && otpSendError) {
      setPhoneError(
        otpSendError?.response?.data?.message ||
        "Mobile number not found. Please sign up."
      );
      setLoading(false);
    }
  }, [otpIsError, otpSendError]);

  useEffect(() => {
    if (verifySuccess) setAuthed(true);
  }, [verifySuccess]);

  useEffect(() => {
    if (verifyIsError && verifyError) {
      setOtpError(
        verifyError?.response?.data?.message?.non_field_errors?.[0] ||
        "Invalid OTP. Please try again."
      );
    }
  }, [verifyIsError, verifyError]);

  useEffect(() => {
    if (authed && redirectTo) history.replace(redirectTo);
  }, [authed, redirectTo, history]);

  /* ── Handlers ── */
  const validatePhone = () => {
    const p = phone.trim();
    if (!p || p.length !== 10 || !/^\d{10}$/.test(p)) {
      setPhoneError("Please enter a valid 10-digit phone number");
      return false;
    }
    setPhoneError("");
    return true;
  };

  const handleSignUp = async () => {
    if (!validatePhone()) return;
    setLoading(true);
    setMobile(phone);
    // Use phone number as initial name - backend will accept it and user can update later
    try { await registerUser({ name: phone, mobile: phone, image: "", location: "" }); } catch (_) {}
  };

  const handleSignIn = async () => {
    if (!validatePhone()) return;
    setLoading(true);
    setMobile(phone);
    try { await sendOtp({ phoneNumber: phone }); } catch (_) {}
  };

  const handleVerify = async () => {
    const otpVal = otp1 + otp2 + otp3 + otp4;
    if (otpVal.length < 4) { setOtpError("Please enter the 4-digit OTP"); return; }
    setOtpError("");
    try { await verifyOtp({ mobile: phone, otp: otpVal }); } catch (_) {}
  };

  const handleOtpChange = (val, setter, nextRef) => {
    const v = val.slice(-1);
    setter(v);
    if (v && nextRef?.current) nextRef.current.focus();
  };

  const handleOtpKeyDown = (e, prevRef) => {
    if (e.key === "Backspace" && e.target.value === "" && prevRef?.current)
      prevRef.current.focus();
  };

  const handlePasswordLogin = async () => {
    if (!pwUsername.trim()) { setPwError("Please enter your username (mobile number)"); return; }
    if (!pwPassword.trim()) { setPwError("Please enter your password"); return; }
    setPwError("");
    setPwLoading(true);
    try {
      await passwordLogin({ username: pwUsername.trim(), password: pwPassword });
      setAuthed(true);
    } catch (err) {
      setPwError(
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        "Login failed. Please check your username and password."
      );
    } finally {
      setPwLoading(false);
    }
  };

  /* ── Authenticated ── */
  if (authed) {
    if (redirectTo) return null;
    return <>{children}</>;
  }

  /* ── Auth UI ── */
  return (
    <div className="abg-wrapper">

      {/* ── Blue header ── */}
      <div className="abg-header">
        <div className="abg-logo-circle">
          <img src={white} alt="MasterClick" />
        </div>
        <p className="abg-brand-name">MasterClick</p>
      </div>

      <div className="abg-card">

        {/* ── Card title ── */}
        <div className="abg-section-title-row">
          <span className="abg-diamond">❖</span>
          <span className="abg-section-title">രജിസ്റ്റർ ചെയ്യാം</span>
          <span className="abg-diamond">❖</span>
        </div>

        {/* ══════════════════════════════════════════════════
            TOP SECTION — NEW / EXISTING OTP REGISTRATION
        ══════════════════════════════════════════════════ */}
        <div className="abg-top-section">
          <div className="abg-top-section-header">
            <div className="abg-section-icon">👤</div>
            <div>
              <p className="abg-section-heading">ആദ്യമായാണ് രജിസ്റ്റർ ചെയ്യുന്നതെങ്കിൽ</p>
              <p className="abg-section-sub">
                നിങ്ങളുടെ മൊബൈൽ നമ്പർ നൽകി OTP പരിശോധിച്ച് രജിസ്ട്രേഷൻ പൂർത്തിയാക്കുക.
              </p>
            </div>
          </div>

          {/* Phone input */}
          <div className="abg-phone-row">
            <div className="abg-flag-box">
              <img src={Flag} alt="IN" />
              <span>+91 ▾</span>
            </div>
            <input
              className="abg-phone-input"
              type="number"
              placeholder="Enter Mobile Number"
              value={phone}
              onChange={(e) => { setPhone(e.target.value); setPhoneError(""); }}
              maxLength={10}
            />
          </div>
          {phoneError && <p className="abg-error-text">{phoneError}</p>}

          {/* Sign Up / Sign In buttons */}
          <div className="abg-btn-row">
            <button className="abg-btn-signup" onClick={handleSignUp} disabled={loading}>
              <span className="abg-btn-sub">ആദ്യമായാണ് രജിസ്റ്റർ ചെയ്യുന്നതെങ്കിൽ</span>
              Sign Up
            </button>
            <button className="abg-btn-signin" onClick={handleSignIn} disabled={loading}>
              <span className="abg-btn-sub">നേരത്തെ രജിസ്റ്റർ ചെയ്തിട്ടുണ്ടെങ്കിൽ</span>
              Sign In
            </button>
          </div>

          {/* OTP entry */}
          {step === "otp" && (
            <div className="abg-otp-section">
              <div className="abg-otp-row">
                <input id="abg-otp1" className="abg-otp-box" type="number" maxLength={1}
                  value={otp1}
                  onChange={(e) => handleOtpChange(e.target.value, setOtp1, otp2Ref)}
                  onKeyDown={(e) => handleOtpKeyDown(e, null)} />
                <input id="abg-otp2" ref={otp2Ref} className="abg-otp-box" type="number" maxLength={1}
                  value={otp2}
                  onChange={(e) => handleOtpChange(e.target.value, setOtp2, otp3Ref)}
                  onKeyDown={(e) => handleOtpKeyDown(e, { current: document.getElementById("abg-otp1") })} />
                <input id="abg-otp3" ref={otp3Ref} className="abg-otp-box" type="number" maxLength={1}
                  value={otp3}
                  onChange={(e) => handleOtpChange(e.target.value, setOtp3, otp4Ref)}
                  onKeyDown={(e) => handleOtpKeyDown(e, otp2Ref)} />
                <input id="abg-otp4" ref={otp4Ref} className="abg-otp-box" type="number" maxLength={1}
                  value={otp4}
                  onChange={(e) => handleOtpChange(e.target.value, setOtp4, null)}
                  onKeyDown={(e) => handleOtpKeyDown(e, otp3Ref)} />
              </div>
              {otpError && <p className="abg-error-text" style={{ textAlign: "center" }}>{otpError}</p>}
              <button className="abg-verify-btn" onClick={handleVerify}
                disabled={otp1 + otp2 + otp3 + otp4 === ""}>
                Verify OTP
              </button>
            </div>
          )}

          {/* OTP guidance */}
          <p className="abg-otp-guidance">
            OTP ലഭിക്കാതിരുന്നെങ്കിൽ, നമ്പർ ശരിയാണെന്ന് ഉറപ്പാക്കുക.
          </p>
        </div>

        {/* ── Divider ── */}
        <div className="abg-divider">
          <span className="abg-divider-text">അല്ലെങ്കിൽ</span>
        </div>

        {/* ══════════════════════════════════════════════════
            LOWER SECTION — REGISTERED PROFILE / STAFF LOGIN
        ══════════════════════════════════════════════════ */}
        <div className="abg-bottom-section">
          <div className="abg-top-section-header">
            <div className="abg-section-icon">👤</div>
            <div>
              <p className="abg-section-heading">നേരത്തെ രജിസ്റ്റർ പൂർത്തിയായിട്ടുണ്ടെങ്കിൽ</p>
              <p className="abg-section-sub">
                നിങ്ങളുടെ സ്ഥാപനത്തിന്റെ മൊബൈൽ നമ്പർ Username ആയും,
                നിങ്ങൾക്ക് ലഭിച്ച Password ഉപയോഗിച്ചും ആവശ്യമായ മാറ്റങ്ങൾ വരുത്താം.
              </p>
            </div>
          </div>

          {/* Username */}
          <label className="abg-field-label">
            Username
            <span className="abg-field-hint"> (നിങ്ങളുടെ സ്ഥാപനത്തിന്റെ മൊബൈൽ നമ്പർ)</span>
          </label>
          {/* dummy hidden field — absorbs Chrome credential autofill so real field stays empty */}
          <input type="text" name="username" style={{ display: "none" }} autoComplete="username" readOnly tabIndex={-1} />
          <div className="abg-input-row">
            <span className="abg-input-icon">👤</span>
            <input
              className="abg-text-input"
              type="tel"
              inputMode="numeric"
              placeholder="Enter your mobile number"
              value={pwUsername}
              autoComplete="new-password"
              name="abg-username-field"
              onChange={(e) => { setPwUsername(e.target.value); setPwError(""); }}
            />
          </div>

          {/* Password */}
          <label className="abg-field-label" style={{ marginTop: 12 }}>
            <span className="abg-field-hint">(നിങ്ങൾക്ക് ലഭിച്ച Password)</span>
          </label>
          <div className="abg-input-row abg-pw-row">
            <span className="abg-input-icon">🔒</span>
            <input
              className="abg-text-input"
              type={pwShowPass ? "text" : "password"}
              placeholder="Enter Password"
              value={pwPassword}
              onChange={(e) => { setPwPassword(e.target.value); setPwError(""); }}
            />
            <button className="abg-eye-btn" onClick={() => setPwShowPass(v => !v)} type="button">
              {pwShowPass
                ? <VisibilityOffIcon style={{ fontSize: 20, color: "#64748b" }} />
                : <VisibilityIcon    style={{ fontSize: 20, color: "#64748b" }} />}
            </button>
            <button
              className="abg-login-inline-btn"
              onClick={handlePasswordLogin}
              disabled={pwLoading}
              type="button"
            >
              {pwLoading ? "..." : "ലോഗിൻ ചെയ്യുക"}
            </button>
          </div>

          {pwError && <p className="abg-error-text">{pwError}</p>}

          <button className="abg-forgot-btn" type="button">
            Password മറന്നു പോയോ?
          </button>
        </div>

        {/* ── Support buttons ── */}
        <div className="abg-support-row" style={{ marginTop: 16 }}>
          <a href="tel:+917293838383" className="abg-call-btn">
            <PhoneIcon style={{ fontSize: 18 }} /> വിളിക്കുക
          </a>
          <a href="https://wa.me/917293838383?text=Hello" target="_blank" rel="noreferrer" className="abg-wa-btn">
            <WhatsAppIcon style={{ fontSize: 18 }} /> WhatsApp
          </a>
        </div>
      </div>

      {/* ── Orange Home button ── */}
      <button className="abg-home-btn abg-home-orange" onClick={() => history.push("/")}>
        <HomeIcon style={{ fontSize: 20 }} /> Home
      </button>
    </div>
  );
};

export default AuthGate;
